import { Form, Input, Button, Typography, Carousel, Select, Modal } from "antd";
import FormItem from "antd/lib/form/FormItem";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { ArrowLeftOutlined } from "@ant-design/icons";
import AuthRedirectScreen from "components/auth-redirect-screen";
import {
  clearIsSSOLogin,
  getAccessToken,
  getAssociatedSubscriptionsFromResponse,
  getIsSSOLogin,
  getSubscriptions,
  login,
  markAuthSessionActive,
  setAccessToken,
  setAssociatedSubscriptions,
  setCurrentSubscriptionId,
  setIDToken,
  setRefreshToken,
  peekAuthenticatedAuthRouteRedirect,
  resolveAuthenticatedAuthRouteRedirect
} from "services/auth";
import { ErrorMessages, SentryErrorSources } from "constants/index";
import { AppLogoIcon } from "components/svg-icons";
import { LoginRequest } from "models/user";
import PiiConsent from "components/widgets/pii-consent";
import ErrorManager from "utils/cumulative-error-message/error-manager";
import { useAutoFocusFirstField } from "hooks/useAutoFocusFirstField";
import {
  getGenericPostLoginRedirectUrl,
  getProjectInviteRedirectUrlFromParams,
  hasPendingPostLoginRedirect,
  resolveSubscriptionForProjectInvite
} from "utils/project-invite-redirect";

import { sortSubscriptionsByName } from "utils/utils";

const { Title } = Typography;

function Login() {
  const history = useHistory();
  const location = useLocation();
  const projectInviteRedirectUrl = useMemo(
    () => getProjectInviteRedirectUrlFromParams(location.search, location.hash),
    [location.search, location.hash]
  );
  const genericRedirectUrl = useMemo(
    () => getGenericPostLoginRedirectUrl(location.search),
    [location.search]
  );
  const postLoginRedirectUrl = projectInviteRedirectUrl || genericRedirectUrl;
  const emailFromSso = new URLSearchParams(location.search).get("email");

  const [loader, setLoader] = useState(false);
  const [loginForm] = Form.useForm();
  const [subscriptionForm] = Form.useForm();

  const [loginInfo, setloginInfo] = useState<any>(null);
  const [userSubscriptions, setUserSubscriptions] = useState<any[]>([]);

  const slider: any = useRef();
  const passwordFormContainerRef = useRef<HTMLDivElement | null>(null);
  useAutoFocusFirstField(passwordFormContainerRef, { page: true });

  const isSsoSession = Boolean(getIsSSOLogin());
  // Resume any pending post-login destination (invite or redirectUrl), not only SSO.
  const hasPostLoginRedirect = useMemo(
    () => hasPendingPostLoginRedirect(location.search, location.hash),
    [location.search, location.hash]
  );
  const authenticatedRedirectPath = useMemo(
    () =>
      hasPostLoginRedirect
        ? peekAuthenticatedAuthRouteRedirect(location.search, location.hash)
        : null,
    [hasPostLoginRedirect, location.hash, location.search]
  );

  useEffect(() => {
    if (!hasPostLoginRedirect) {
      if (isSsoSession && !getAccessToken()) {
        clearIsSSOLogin();
      }
      return;
    }

    if (!getAccessToken()) {
      if (isSsoSession) {
        clearIsSSOLogin();
      }
      return;
    }

    const redirectPath = resolveAuthenticatedAuthRouteRedirect(
      location.search,
      location.hash
    );
    if (redirectPath) {
      history.replace(redirectPath);
    }
  }, [
    hasPostLoginRedirect,
    history,
    isSsoSession,
    location.hash,
    location.search
  ]);

  useEffect(() => {
    if (emailFromSso) {
      loginForm.setFieldsValue({
        email: emailFromSso
      });
    }
  }, [emailFromSso, loginForm]);

  const goToPostLoginDestination = useCallback(() => {
    history.push(postLoginRedirectUrl || "/projects");
  }, [history, postLoginRedirectUrl]);

  const handleLoginFailure = useCallback(
    (res: any, emailToUse: string) => {
      setLoader(false);
      const status = res.response?.status;

      if (status === 401) {
        history.push(
          `/?error=${encodeURIComponent(
            res.response?.data?.error
          )}&email=${encodeURIComponent(emailToUse)}`
        );
        return;
      }

      if (status === 403) {
        history.push(`/account-locked?email=${encodeURIComponent(emailToUse)}`);
        return;
      }

      Modal.error({ content: res.response?.data?.error || res.message });
      ErrorManager().handleError({
        error: res,
        showError: false,
        errorSource: SentryErrorSources.auth
      });
    },
    [history]
  );

  const routeAfterSubscriptions = useCallback(
    (subscriptions: any[]) => {
      if (subscriptions.length === 1) {
        // single subscriptions
        setCurrentSubscriptionId(subscriptions[0].id);
        goToPostLoginDestination();
        return;
      }

      const inviteSubscriptionId = resolveSubscriptionForProjectInvite(
        projectInviteRedirectUrl,
        subscriptions
      );
      if (inviteSubscriptionId && postLoginRedirectUrl) {
        setCurrentSubscriptionId(inviteSubscriptionId);
        history.push(postLoginRedirectUrl);
        return;
      }

      if (!subscriptions.length) {
        setLoader(false);
        Modal.error({ content: "No subscriptions found for this account" });
        return;
      }

      loginForm.resetFields();
      const sortedSubscriptions = sortSubscriptionsByName(subscriptions);
      if (sortedSubscriptions.length === 0) {
        setLoader(false);
        Modal.error({
          content: "No subscriptions are associated with this account."
        });
        return;
      }
      setUserSubscriptions(sortedSubscriptions);
      subscriptionForm.setFieldsValue({
        subscriptionId: sortedSubscriptions[0].id
      });
      setLoader(false);
      slider.current.next();
    },
    [
      goToPostLoginDestination,
      history,
      loginForm,
      postLoginRedirectUrl,
      projectInviteRedirectUrl,
      subscriptionForm
    ]
  );

  const onLoginFinish = useCallback(
    async (event: LoginRequest) => {
      setLoader(true);
      const emailToUse = event.email || emailFromSso || "";
      const res: any = await login({
        email: emailToUse,
        password: event.password
      });

      if (res.status !== 200) {
        handleLoginFailure(res, emailToUse);
        return;
      }

      setIDToken(res?.data.success.id_token);
      setAccessToken(res?.data.success.access_token);
      setRefreshToken(res?.data.success.refresh_token);
      markAuthSessionActive();

      if (res.data.success.is_password_expired) {
        history.push("/reset_expired_password");
        return;
      }

      const accessToken = res?.data.success.access_token;
      const subscriptionsRes: any = await getSubscriptions(accessToken);

      if (subscriptionsRes?.status !== 200) {
        setLoader(false);
        Modal.error({
          content:
            subscriptionsRes?.response?.data?.error ||
            subscriptionsRes?.message ||
            "Unable to fetch subscriptions"
        });
        ErrorManager().handleError({
          error: subscriptionsRes,
          showError: false
        });
        return;
      }

      const subscriptions =
        getAssociatedSubscriptionsFromResponse(subscriptionsRes);
      setAssociatedSubscriptions(subscriptions);

      setloginInfo({
        token: accessToken,
        is_password_expired: res.data.success.is_password_expired
      });

      routeAfterSubscriptions(subscriptions);
    },
    [emailFromSso, handleLoginFailure, history, routeAfterSubscriptions]
  );

  const goBackToLogin = () => {
    history.push("/");
  };

  if (authenticatedRedirectPath) {
    return <AuthRedirectScreen />;
  }

  return (
    <div className="login">
      <div className="login-form">
        <div className="flex items-center gap-1 mb-2">
          <Button
            icon={
              <ArrowLeftOutlined
                style={{ color: "var(--ciq-black)", opacity: "45%" }}
              />
            }
            className="text-one text-black !bg-transparent !border-0 px-0 !shadow-none"
            onClick={goBackToLogin}
          />
          <div>{emailFromSso}</div>
        </div>
        <div style={{ margin: "auto", display: "block" }}>
          <AppLogoIcon />
        </div>
        <div className="login-logo">
          <Title level={3} className="mt-3">
            Login
          </Title>
        </div>
        <Carousel
          dots={false}
          afterChange={() => {}}
          ref={(ref) => {
            slider.current = ref;
          }}
          speed={250}
          infinite={false}
          swipe={false}
          swipeToSlide={false}
          draggable={false}
        >
          <div className="px-1" ref={passwordFormContainerRef}>
            <Form
              onFinish={onLoginFinish}
              form={loginForm}
              labelCol={{ span: 50 }}
              wrapperCol={{ span: 30 }}
              scrollToFirstError
              layout="vertical"
            >
              <FormItem
                name="password"
                label="Password"
                className="password"
                layout="vertical"
                validateStatus=""
                rules={[
                  {
                    required: true,
                    validateTrigger: "onSubmit",
                    message: ErrorMessages.PasswordEmptyCheck
                  }
                ]}
              >
                <Input.Password
                  placeholder="Enter Password"
                  tabIndex={loginInfo ? -1 : 0}
                  data-autofocus="true"
                />
              </FormItem>
              <div className="flex justify-end items-center w-full mt-2">
                <a
                  href="/forgot_password"
                  className="text-gray-800"
                  tabIndex={loginInfo ? -1 : 0}
                >
                  Forgot password?
                </a>
              </div>
              <div className="pt-4">
                <Button
                  type="primary"
                  htmlType="submit"
                  size="middle"
                  loading={loader}
                  disabled={loader}
                  tabIndex={loginInfo ? -1 : 0}
                  className="w-full"
                >
                  Login
                </Button>
              </div>
            </Form>
          </div>
          <div className="px-1">
            <Form
              onFinish={(subscriptionData: any) => {
                setCurrentSubscriptionId(subscriptionData.subscriptionId);
                goToPostLoginDestination();
              }}
              form={subscriptionForm}
              labelCol={{ span: 50 }}
              wrapperCol={{ span: 30 }}
              scrollToFirstError
              layout="vertical"
              className="-mt-10"
            >
              <FormItem
                name="subscriptionId"
                label="Choose a subscription"
                layout="vertical"
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  className="w-full"
                  onChange={() => {}}
                  options={userSubscriptions.map((sub: any) => {
                    return {
                      value: sub.id,
                      label: sub.name
                    };
                  })}
                  tabIndex={!loginInfo ? -1 : 0}
                />
              </FormItem>

              <div className="pt-4 w-full grid grid-cols-3">
                <div />
                <Button
                  htmlType="submit"
                  size="middle"
                  loading={loader}
                  disabled={loader}
                  tabIndex={!loginInfo ? -1 : 0}
                  className="w-full"
                >
                  Continue
                </Button>
                <div />
              </div>
            </Form>
          </div>
        </Carousel>
      </div>
      <PiiConsent className="w-[350px] text-center text-xs text-ciq-gray-600 pt-7" />
    </div>
  );
}

export default Login;
